import { apiClient } from '@api'

/**
 * Risk Exposure Report Service
 *
 * API service for AI-powered risk exposure report generation and retrieval.
 * Provides methods for generating, fetching, and managing risk exposure reports.
 */
const riskExposureService = {
	// ====================================================================
	// REPORT RETRIEVAL
	// ====================================================================

	/**
	 * Get the latest completed risk exposure report
	 * @returns {Promise} API response with latest report or null if none exists
	 */
	getLatest: () => {
		return apiClient.get('/risk-exposure/latest')
	},

	/**
	 * Get report generation status
	 * @returns {Promise} API response with generation status
	 * Returns: { is_generating, report_id, status, started_at, has_report }
	 */
	getStatus: () => {
		return apiClient.get('/risk-exposure/status')
	},

	/**
	 * Get a specific report by ID
	 * @param {number} reportId - The report ID to fetch
	 * @returns {Promise} API response with full report data
	 */
	getById: (reportId) => {
		return apiClient.get(`/risk-exposure/${reportId}`)
	},

	/**
	 * Get report generation history (last N reports)
	 * @param {number} limit - Number of reports to fetch (default: 5, max: 10)
	 * @returns {Promise} API response with array of historical reports
	 */
	getHistory: (limit = 5) => {
		return apiClient.get(`/risk-exposure/history?limit=${limit}`)
	},

	// ====================================================================
	// REPORT GENERATION
	// ====================================================================

	/**
	 * Trigger new risk exposure report generation
	 * This is an async operation - the report is generated in the background
	 * @returns {Promise} API response with generation status
	 * Returns: { status: 'processing'|'already_processing'|'completed', report_id, started_at }
	 */
	generate: () => {
		return apiClient.post('/risk-exposure/generate')
	},

	// ====================================================================
	// UTILITY METHODS
	// ====================================================================

	/**
	 * Poll for report completion status
	 * @param {number} reportId - The report ID to check
	 * @param {number} interval - Polling interval in ms (default: 5000)
	 * @param {number} maxAttempts - Maximum polling attempts (default: 60 = 5 minutes)
	 * @returns {Promise} Resolves when report is complete or rejects on timeout/failure
	 */
	pollForCompletion: async (reportId, interval = 5000, maxAttempts = 60) => {
		let attempts = 0

		return new Promise((resolve, reject) => {
			const checkStatus = async () => {
				attempts++

				try {
					const response = await apiClient.get('/risk-exposure/status')
					const status = response.data

					if (!status.is_generating) {
						if (status.status === 'completed') {
							// Fetch the full report
							const reportResponse = await apiClient.get(`/risk-exposure/${reportId}`)
							resolve(reportResponse.data)
						} else if (status.status === 'failed') {
							reject(new Error('Report generation failed'))
						} else {
							resolve(status)
						}
						return
					}

					if (attempts >= maxAttempts) {
						reject(new Error('Report generation timed out'))
						return
					}

					setTimeout(checkStatus, interval)
				} catch (error) {
					reject(error)
				}
			}

			checkStatus()
		})
	},

	/**
	 * Get risk level color class
	 * @param {string} level - Risk level ('High', 'Medium', 'Low')
	 * @returns {string} CSS class name for the risk level
	 */
	getRiskLevelClass: (level) => {
		const levelMap = {
			high: 'risk-high',
			medium: 'risk-medium',
			low: 'risk-low',
			unknown: 'risk-unknown'
		}
		return levelMap[level?.toLowerCase()] || levelMap.unknown
	},

	/**
	 * Get risk severity color class
	 * @param {string} severity - Severity level ('high', 'medium', 'low')
	 * @returns {string} CSS class name for the severity
	 */
	getSeverityClass: (severity) => {
		const severityMap = {
			high: 'severity-high',
			medium: 'severity-medium',
			low: 'severity-low'
		}
		return severityMap[severity?.toLowerCase()] || ''
	},

	/**
	 * Format risk score for display
	 * @param {number} score - Risk score (0-100)
	 * @returns {string} Formatted score with label
	 */
	formatRiskScore: (score) => {
		if (score >= 70) return { score, label: 'High Risk', level: 'high' }
		if (score >= 40) return { score, label: 'Medium Risk', level: 'medium' }
		return { score, label: 'Low Risk', level: 'low' }
	},

	/**
	 * Get urgency badge variant
	 * @param {string} urgency - Urgency level
	 * @returns {string} Badge variant class
	 */
	getUrgencyVariant: (urgency) => {
		const urgencyMap = {
			immediate: 'danger',
			'this week': 'warning',
			'this month': 'info',
			monitor: 'success',
			default: 'secondary'
		}
		return urgencyMap[urgency?.toLowerCase()] || urgencyMap.default
	}
}

export default riskExposureService
